import * as ko from 'knockout';
import page from 'page';

import { app } from '../app';
import { session } from '../session';
import { getExpirationDays } from '../models/tenant';
import * as tenantsApi from '../api/base_tenants';
import i18n from '../i18n';
import { logError } from '../error_logging';

let template = require('raw-loader!../../templates/login.html').default;

interface LoginParams {
  motto: string;
  showFooter: boolean;
}

let loginParams: LoginParams;

export function setLoginParams(params: LoginParams) {
  loginParams = params;
}
class LoginScreen {
  allowSignUp = SERVER_INFO.ALLOW_SIGNUP;
  supportEmail = SERVER_INFO.SUPPORT_EMAIL;
  loading = ko.observable<boolean>(false);
  loggedIn = session.loggedIn;
  authorized = session.authorized;
  needsEmailVerification = session.needsEmailVerification;
  userEmail = ko.observable('');
  authorizedTenants = ko.pureComputed(() => {
    return this.getAuthorizedTenantsList();
  });
  firebaseTenantId = ko.observable<string>();
  tenantsByEmail = ko.observableArray<tenantsApi.TenantPublicData>();
  loadingTenantsByEmail = ko.observable(false);
  tenantsByEmailError = ko.observable('');
  loginEmail = ko.observable('').extend({ required: true, serverError: true });
  mobileOnlyTenants = session.mobileOnlyTenants;
  otherTenants = ko.observableArray<tenantsApi.TenantPublicData>();

  private subscription: KnockoutSubscription = null;
  public params: LoginParams;

  constructor() {
    this.params = loginParams;
    this.subscription = session.loggedIn.subscribe(this.onLoginStateChange.bind(this));
    this.onLoginStateChange(session.loggedIn());
  }

  async loadTenantsByEmail() {
    this.loadingTenantsByEmail(true);
    try {
      const tenants = await tenantsApi.tenantsByEmail(this.loginEmail());
      this.tenantsByEmail(tenants);
      if (tenants.length === 0) {
        this.tenantsByEmailError(i18n.t('Please check your email address.')());
      } else if (tenants.length === 1) {
        this.firebaseTenantId(tenants[0].firebase_id);
      }
    } finally {
      this.loadingTenantsByEmail(false);
    }
  }

  setSelectedTenant = (() => (tenant: tenantsApi.TenantPublicData) => {
    this.firebaseTenantId(tenant.firebase_id);
  })().bind(this);

  switchToTenant = async (tenant: tenantsApi.TenantPublicData) => {
    // Opening another Firebase tenant requires signing in again, and the email list has to stay
    // non-empty for the logged-out screen to offer the password prompt.
    this.loginEmail(session.getEmail());
    this.tenantsByEmail(this.otherTenants());
    await session.logout();
    this.firebaseTenantId(tenant.firebase_id);
  };

  switchToTenantByKeyboard = (tenant: tenantsApi.TenantPublicData, event: KeyboardEvent) => {
    // Knockout's click binding does not fire on keyboard activation, so the row needs its own key
    // handler; returning true for anything else leaves Tab and shortcuts working.
    if (event.key !== 'Enter' && event.key !== ' ') {
      return true;
    }
    this.switchToTenant(tenant);
    return false;
  };

  private getAuthorizedTenantsList() {
    let tenants = session.tenants();
    tenants.map((tenant) => {
      tenant.expirationTitle = tenant.is_expired
        ? i18n.t('Subscription of this organization is expired')()
        : '';
    });
    return tenants;
  }

  resendEmailVerification() {
    session.sendEmailVerification();
  }

  private onLoginStateChange(loggedIn: boolean) {
    this.userEmail(session.getEmail());

    if (!loggedIn || session.needsEmailVerification()) {
      return;
    }

    this.loading(true);
    const authorizationsRequest = session
      .getToken()
      .then((token) => tenantsApi.authorizations(token, true));

    Promise.all([authorizationsRequest, this.loadTenantsRequiringSignIn()]).then(
      ([{ tenants, mobile_only_tenants }, tenantsForEmail]) => {
        this.loading(false);

        if (!session.loggedIn()) {
          // user logged out while we were loading list
          return;
        }

        this.userEmail(session.getEmail());
        session.tenants(tenants);
        session.mobileOnlyTenants(mobile_only_tenants);
        this.otherTenants(this.withoutAccessibleTenants(tenantsForEmail));
        this.openTenantOrLeavePicker(tenants);
      }
    );
  }

  private openTenantOrLeavePicker(tenants: tenantsApi.TenantData[]) {
    const slug = session.getLocationTenantSlug(location.pathname);
    if (!slug) {
      if (tenants.length > 0 && this.shouldOpenTenantWithoutPicking(tenants)) {
        this.openDirect(tenants[0]);
      }
      return;
    }

    const deepLinkedTenant = tenants.find((tenant) => tenant.slug === slug);
    if (deepLinkedTenant) {
      this.openDirect(deepLinkedTenant);
    } else if (tenants.length > 0) {
      // user has selected a tenant he doesn't have access to
      // show him tenant list instead
      page('/');
    }
  }

  // A password token is scoped to one Firebase tenant, so the authorized list can only ever
  // report that one organization. Failures fall back to an empty list so they cannot block the
  // sign-in this runs alongside.
  private loadTenantsRequiringSignIn(): Promise<tenantsApi.TenantPublicData[]> {
    const arrivedAlreadySignedIn = this.firebaseTenantId() == null;
    if (
      !APP_CONFIG.ALLOW_PASSWORD_LOGIN ||
      !arrivedAlreadySignedIn ||
      !session.isTenantScopedUser()
    ) {
      return Promise.resolve([]);
    }
    return tenantsApi.tenantsByEmail(session.getEmail()).catch((error) => {
      // An empty fallback looks exactly like a user who has a single organization, so a failure
      // here stays invisible unless it is logged.
      logError('Could not load the organizations that require signing in again', error);
      return [] as tenantsApi.TenantPublicData[];
    });
  }

  private withoutAccessibleTenants(tenantsForEmail: tenantsApi.TenantPublicData[]) {
    const accessibleSlugs = session
      .tenants()
      .concat(session.mobileOnlyTenants())
      .map((accessibleTenant) => accessibleTenant.slug);
    return tenantsForEmail.filter((tenant) => accessibleSlugs.indexOf(tenant.slug) < 0);
  }

  // Opening outright is the exception, for a user with nothing to choose between: anyone who can
  // reach a second organization — even one needing a fresh sign-in — sees the list.
  private shouldOpenTenantWithoutPicking(tenants: tenantsApi.TenantData[]): boolean {
    if (this.firebaseTenantId() != null) {
      // Set only when this screen drove the sign-in, so the organization is already chosen.
      return true;
    }
    return tenants.length === 1 && this.otherTenants().length === 0;
  }

  private openDirect(tenant: tenantsApi.TenantData) {
    // Preserve the current history state (notably its positionInHistory) so this
    // post-login replace doesn't look like a forward navigation: a deep-linked
    // page opened straight after login must stay the first in-app entry, so its
    // cancel/back falls back to the tenant home instead of leaving the app.
    if (location.pathname === '/') {
      (<any>page).replace(session.toTenantPath('/', tenant), window.history.state);
    } else {
      (<any>page).replace(location.pathname + location.search, window.history.state);
    }
  }

  dispose() {
    if (this.subscription) {
      this.subscription.dispose();
    }
  }
}

export let login = {
  name: 'login',
  viewModel: LoginScreen,
  template: template,
};

ko.components.register(login.name, login);

export function requireAuthorized(ctx: PageJS.Context, next: () => any) {
  if (session.authorized()) {
    let days = getExpirationDays();
    if (days !== null && days <= 0) {
      page.redirect(session.toTenantPath('/subscription/'));
    }
    next();
  } else {
    app.currentPage({ name: login.name });
  }
}

export function requireGDMAuthorized(ctx: PageJS.Context, next: () => any) {
  // only for GDM users
  if (session.authorized()) {
    let days = getExpirationDays();
    if (days !== null && days <= 0) {
      page.redirect(session.toTenantPath('/subscription/'));
    }

    if (session.tenant().slug === 'gdm_seeds') {
      next();
    } else {
      page.redirect('/');
    }
  } else {
    app.currentPage({ name: login.name });
  }
}

export function requireAuthorizedCanBeExpired(ctx: PageJS.Context, next: () => any) {
  if (session.authorized()) {
    next();
  } else {
    app.currentPage({ name: login.name });
  }
}

export function requireLoggedIn(ctx: PageJS.Context, next: () => any) {
  if (session.loggedIn()) {
    next();
  } else {
    page.redirect('/');
  }
}
